
import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { NavMain } from "@/components/marketplace/nav-main/nav-main";
import { NavUser } from "@/components/marketplace/nav-user/nav-user";
import { TeamSwitcher } from "@/components/marketplace/team-switcher/team-switcher";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import { useUserRole } from "@/hooks/useUserRole";
import { getNavigationConfig } from "./config/navigationConfig";
import { useState } from "react";
import { useInvestmentHoldings } from "@/providers/manual/hooks/useInvestmentHoldings";
import { useAddPositions } from "@/hooks/investments/useAddPositions";
import { AssetTypeFilterDialog } from "@/components/investments/AssetTypeFilterDialogNew";
import { createBuildPortfolioHandlers } from "@/utils/buildPortfolioHandlers";
import { logger } from "@/services/LoggingService";

const AppSidebar = ({ ...props }: React.ComponentProps<typeof Sidebar>) => {
  const { userRole } = useUserRole();
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile, setOpenMobile } = useSidebar();
  const [showAssetTypeFilterDialog, setShowAssetTypeFilterDialog] = useState(false);
  const [collectedPositions, setCollectedPositions] = useState<any[]>([]);
  const { openAddPositions } = useAddPositions();
  
  // Investment holdings hook
  const { holdings, refreshHoldings } = useInvestmentHoldings();
  
  const isSettingsPage = location.pathname.startsWith('/settings');
  const isPortfoliosSection = location.pathname.startsWith('/portfolios');
  const isIndexPage = location.pathname === "/";
  const isBuildPortfolioPage = location.pathname.startsWith('/portfolios/build');

  const handleNavigation = React.useCallback((path: string) => {
    navigate(path);
    if (isMobile) {
      setOpenMobile(false);
    }
  }, [navigate, isMobile, setOpenMobile]);
  
  // Build portfolio handlers
  const { handleBuildPortfolio } = createBuildPortfolioHandlers({
    investmentHoldings: holdings,
    refreshHoldings,
    setCollectedPositions,
    setShowAssetTypeFilterDialog
  });
  
  // Handler for opening the portfolio creation dialog
  const handleBuildPortfolioClick = React.useCallback(async (e: React.MouseEvent) => {
    e.preventDefault();
    await handleBuildPortfolio();
    return Promise.resolve();
  }, [handleBuildPortfolio]);

  const { teams, user, navItems } = getNavigationConfig(
    location.pathname,
    isSettingsPage,
    isPortfoliosSection,
    isIndexPage,
    isBuildPortfolioPage,
    handleBuildPortfolioClick, // Use our new dialog handler instead
    userRole
  );

  return (
    <>
      <Sidebar 
        collapsible="icon" 
        className="flex-shrink-0 border-r"
        {...props}
      >
        <SidebarHeader className="-mb-2">
          <TeamSwitcher teams={teams} />
        </SidebarHeader>
        <SidebarContent>
          <NavMain items={navItems} onNavigate={handleNavigation} />
        </SidebarContent>
        <SidebarFooter className="mt-auto">
          <NavUser user={user} />
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>

      {/* Build Portfolio Flow Dialogs */}
      <AssetTypeFilterDialog
        open={showAssetTypeFilterDialog}
        onOpenChange={setShowAssetTypeFilterDialog}
        positions={collectedPositions}
        onContinue={(positions) => {
          setShowAssetTypeFilterDialog(false);
          openAddPositions({
            positions,
            navigateOnZeroAdded: true,
            onClose: () => {
              setCollectedPositions([]);
            }
          });
        }}
      />
    </>
  );
};

export { AppSidebar };
